import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Site-wide canonical origin. All `link rel="canonical"` and `og:url`
 * point back here so search engines consolidate ranking signals on a
 * single hostname (apex faticaftan.com), regardless of how the visitor
 * arrived — www, fati-caftan.com alias, or bare IP all 301 here anyway.
 */
const CANONICAL_ORIGIN = 'https://faticaftan.com';
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og-default.jpg`;

interface SEOProps {
  /** i18n key resolving to the page-specific title. Site name is appended. */
  titleKey?: string;
  /** Literal title that bypasses i18n (useful for dynamic content like product names). */
  title?: string;
  /** i18n key resolving to the meta description. Defaults to seo.defaults.description. */
  descriptionKey?: string;
  /** Literal description that bypasses i18n. */
  description?: string;
  /** Optional absolute URL of the OG image for this page. Falls back to the
   *  site default placeholder which lives at /og-default.jpg. */
  image?: string;
  /** True for placeholder / admin / under-construction pages — emits
   *  `<meta name="robots" content="noindex,nofollow">`. */
  noindex?: boolean;
  /** Optional JSON-LD payload merged into the head. Pass `Schema.org/Product`
   *  on product detail pages, etc. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Override the canonical path. Defaults to the current `useLocation` pathname. */
  canonicalPath?: string;
  /** Override the og:type. Defaults to `website` (use `product` on product pages). */
  ogType?: 'website' | 'article' | 'product';
}

/**
 * Per-page `<head>` manager. Mounted inside any public route to set
 * title / meta description / canonical / hreflang / Open Graph / Twitter
 * Card / optional JSON-LD.
 *
 * Why this exists: SPA routes share the same index.html shell, so the
 * head tags need to be updated on every navigation. react-helmet-async
 * handles the lifecycle so the previous page's tags get removed cleanly
 * — no leaked `<title>` from a stale route.
 *
 * Visible-content responsibility stays with the page component; this one
 * only deals with crawlers + social previews.
 */
export function SEO({
  titleKey,
  title,
  descriptionKey = 'seo.defaults.description',
  description: descriptionLiteral,
  image,
  noindex = false,
  jsonLd,
  canonicalPath,
  ogType = 'website',
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const pageTitle = title ?? (titleKey ? t(titleKey) : t('seo.defaults.siteName'));
  const siteName = t('seo.defaults.siteName');
  // Append site name unless the page-specific title already mentions it
  // (keeps the home page from rendering "Fati Caftan · Fati Caftan").
  const fullTitle = pageTitle.toLowerCase().includes(siteName.toLowerCase())
    ? pageTitle
    : `${pageTitle} · ${siteName}`;
  const description = descriptionLiteral ?? t(descriptionKey);
  const path = canonicalPath ?? location.pathname;
  const canonicalUrl = `${CANONICAL_ORIGIN}${path === '/' ? '' : path}`;
  const ogImage = image ?? DEFAULT_OG_IMAGE;
  const lang = i18n.language;

  return (
    <Helmet>
      <html lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang: same canonical URL since we don't serve locale-prefixed
          paths. Visitors switch via the in-page language selector which
          persists in localStorage. x-default points to the same URL. */}
      <link rel="alternate" hrefLang="fr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="ar" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph — Facebook, WhatsApp, LinkedIn, Discord previews. */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={lang === 'ar' ? 'ar_MA' : lang === 'fr' ? 'fr_MA' : 'en_US'} />

      {/* Twitter Card — X / Twitter preview, also used by some other apps. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
